import type { LoginRequest, ModuleKey, Permission, SessionInfo } from '@sahab/contracts'
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { allows, allowsAny, moduleEnabled, type Requirement } from '../../app/access'
import { api } from '../../lib/api'

export const sessionKey = ['session'] as const

/**
 * GET /auth/me. A 401 here means "not signed in" — the route guard handles it, so the global
 * session-expiry redirect is skipped for this call.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: sessionKey,
  queryFn: ({ signal }) => api.get<SessionInfo>('/auth/me', { signal, skipAuthRedirect: true }),
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
  retry: false,
})

/** The signed-in session. Only valid under the authenticated layout, which guarantees it is loaded. */
export function useSession(): SessionInfo {
  return useSuspenseQuery(sessionQueryOptions).data
}

/** UX only: hides what the user cannot do. The API enforces every permission. */
export function useCan(permission: Permission): boolean {
  return useSession().permissions.includes(permission)
}

/** Every permission of a requirement (e.g. a `CREATE_ACCESS` entry): the same rule as `requireAccess`. */
export function useAllows(required: Requirement): boolean {
  return allows(useSession().permissions, required)
}

/** At least one of the permissions (e.g. `LOOKUP_ACCESS`): the API's `@RequireAnyPermission` rule. */
export function useAllowsAny(anyOf: readonly Permission[]): boolean {
  return allowsAny(useSession().permissions, anyOf)
}

/**
 * Holds `permission` over every record (no data scope narrower than ALL, ADR-025): the API's
 * `reachesAll`. Readers covering every record (aggregate reports, supplier and customer balances, the
 * aging and statements) answer 403 SCOPE_RESTRICTED otherwise, so screens leave them out. A permission no
 * scope narrows (customers, the till: `isScopeEnforced`) is never in `scopedPermissions`, so it always
 * reaches every record. UX only.
 */
export function reachesAll(session: SessionInfo, permission: Permission): boolean {
  return session.permissions.includes(permission) && !session.scopedPermissions?.includes(permission)
}

export function useReachesAll(permission: Permission): boolean {
  return reachesAll(useSession(), permission)
}

export function usePermissions(): ReadonlySet<Permission> {
  return new Set(useSession().permissions)
}

/**
 * What the user's roles grant, whatever the company's modules (A11): a switched-off module's
 * permissions are here, not in `usePermissions`. For anti-escalation (the role editor offers what the
 * user may grant) and for telling "turned off" from "no permission". Older sessions: the effective ones.
 */
export function useGrantedPermissions(): ReadonlySet<Permission> {
  const session = useSession()
  return new Set(session.grantedPermissions ?? session.permissions)
}

/**
 * Whether the company has a module (D15), e.g. `workflows`: a super admin can turn it off per
 * company. UX only — the API refuses a disabled module's routes.
 */
export function useModuleEnabled(module: ModuleKey): boolean {
  return moduleEnabled(useSession().modules, module)
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: LoginRequest) =>
      api.post<SessionInfo>('/auth/login', body, { skipAuthRedirect: true }),
    onSuccess: (info) => {
      // A different user may have been signed in on this tab before: start from a clean cache.
      queryClient.clear()
      queryClient.setQueryData(sessionKey, info)
    },
  })
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout', undefined, { skipAuthRedirect: true }),
  })
}

/**
 * Only same-origin, absolute paths are accepted as a post-login destination (no open redirects,
 * no protocol-relative `//host` or `/\host` tricks), and never the login page itself.
 */
export function safeRedirect(target: string | undefined | null): string {
  if (!target?.startsWith('/') || target.startsWith('//') || target.startsWith('/\\')) return '/'
  if (target === '/login' || target.startsWith('/login?') || target.startsWith('/login/')) return '/'
  return target
}
